// NewAiPrompt/conversationStore.js
//
// Per-block AI Assistant conversation store (feature-flagged; see ./flag.js).
//
// WHY THIS EXISTS
// The AI Assistant popup (NewAiPrompt) is conditionally mounted by its parent
// block settings:  `{showAiPrompt && createPortal(<NewAIPrompt/>)}`.  Closing the
// popup flips showAiPrompt -> false, which UNMOUNTS the component.  When every
// piece of conversation state lived in local `useState` (textvalue / title /
// aiGeneratedResponse), that unmount destroyed the whole thread, so reopening the
// popup showed a blank panel and the user could not continue a back-and-forth
// (the reported bug: "AI assistant does not keep its memory ... loses it right
// when you close").
//
// This module lifts the conversation OUT of component state into a module-level
// store keyed by a stable per-block id (the craft.js node id).  Because the store
// outlives any single mount, closing + reopening the popup — and re-selecting the
// block — preserves the thread for the editing session.
//
// Scope / non-goals:
//   * In-memory, per editing session ONLY.  It is intentionally NOT persisted to
//     localStorage or the server (a page reload starts fresh).  A durable,
//     server-side thread is a documented follow-up (see FINDINGS / PR notes).
//   * The store is pure and framework-agnostic (a Map + a tiny pub/sub) so it is
//     unit-testable without React.  NewAiPrompt subscribes via a thin hook.

/** @typedef {{ role: "user"|"assistant", content: string, ts: number }} Message */

const conversations = new Map(); // key -> { messages: Message[], draft: string }
const listeners = new Map(); // key -> Set<(conv) => void>

const EMPTY = Object.freeze({ messages: Object.freeze([]), draft: "" });

/** Read the conversation for a block. Returns a shared EMPTY value when unknown. */
export function getConversation(key) {
  if (!key) return EMPTY;
  return conversations.get(key) || EMPTY;
}

function emit(key, next) {
  const subs = listeners.get(key);
  if (subs) subs.forEach((fn) => fn(next));
}

function write(key, next) {
  conversations.set(key, next);
  emit(key, next);
  return next;
}

/** Store the in-progress input text (so a half-typed prompt survives close). */
export function setDraft(key, draft) {
  if (!key) return EMPTY;
  const prev = getConversation(key);
  return write(key, { messages: prev.messages, draft: draft == null ? "" : draft });
}

/** Append a turn to the thread. Returns the new conversation snapshot. */
export function appendMessage(key, role, content) {
  if (!key) return EMPTY;
  const prev = getConversation(key);
  return write(key, {
    messages: [...prev.messages, { role, content, ts: Date.now() }],
    draft: prev.draft,
  });
}

/** Reset a block's thread + draft. */
export function clearConversation(key) {
  if (!key) return EMPTY;
  return write(key, { messages: [], draft: "" });
}

/** The most recent assistant turn's text (used for the Copy button), or "". */
export function lastAssistantMessage(key) {
  const { messages } = getConversation(key);
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") return messages[i].content;
  }
  return "";
}

// The AI backend endpoint (generateAICompletion) is STATELESS — it accepts a
// single `prompt` string and holds no server-side thread. To give the model
// context from earlier turns WITHOUT a backend change, compose the recent
// history into the prompt we send. Bounded by turn + char caps so the prompt
// cannot grow without limit over a long editing session.
const MAX_TURNS = 8; // include at most the last N messages (user + assistant)
const MAX_CHARS = 6000; // hard cap on the composed history length

export function composeContextPrompt(key, newPrompt, opts = {}) {
  const maxTurns = opts.maxTurns == null ? MAX_TURNS : opts.maxTurns;
  const maxChars = opts.maxChars == null ? MAX_CHARS : opts.maxChars;
  const { messages } = getConversation(key);
  if (!messages || messages.length === 0) return newPrompt; // first turn: raw prompt
  const recent = messages.slice(-maxTurns);
  let history = recent
    .map((m) => `${m.role === "assistant" ? "Assistant" : "User"}: ${m.content}`)
    .join("\n");
  if (history.length > maxChars) history = history.slice(history.length - maxChars);
  return `Continue this conversation about a piece of website text. Prior turns:\n${history}\n\nUser: ${newPrompt}\nAssistant:`;
}

/** Subscribe to changes for a key. Returns an unsubscribe fn. */
export function subscribe(key, fn) {
  if (!key) return () => {};
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(fn);
  return () => {
    const subs = listeners.get(key);
    if (!subs) return;
    subs.delete(fn);
    if (subs.size === 0) listeners.delete(key);
  };
}

// Test-only helper so suites can start from a clean slate without leaking state
// across `it` blocks (the store is module-level and long-lived by design).
export function __resetAllConversations() {
  conversations.clear();
  listeners.clear();
}
